import {
  getLeoUuid,
  LeoUUID,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class AddBOUserToProjectRPC implements
  LeoRPC<AddBOUserToProjectRPC.Request, AddBOUserToProjectRPC.Response, AddBOUserToProjectRPC.Errors.Errors>
{
  abstract execute(
    request: AddBOUserToProjectRPC.Request
  ): Promise<LeoRPCResult<AddBOUserToProjectRPC.Response, AddBOUserToProjectRPC.Errors.Errors>>;
}

export namespace AddBOUserToProjectRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    boUserIds: string[];
  }
  
  export class Request implements LeoRPCRequest {
    readonly isAddBOUserToProjectRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly boUserIds: LeoUUID[];
  
    static readonly constraints = {
      "boUserIds": {
        "minSize": 1,
        "maxSize": 128,
      }
    }
  
    constructor(
    projectId: LeoUUID, 
    boUserIds: LeoUUID[]
    ) {
      this.projectId = projectId;
      Request.validateBoUserIds(boUserIds);
      this.boUserIds = boUserIds;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const boUserIds = getList<string, LeoUUID>(
        dto,
        "boUserIds",
        (e: string) => new LeoUUID(e),
        InvalidRequestBoUserIdsError
      );
      return new Request(
        projectId,
        boUserIds
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          boUserIds: this.boUserIds.map((e: LeoUUID) => { return e.toDTO(); })
      };
      return dto;
    }
    private static validateBoUserIds = (boUserIds: LeoUUID[]): void => {
      if(boUserIds.length < 1) {
        throw new InvalidRequestBoUserIdsMinSizeError(
          1,
          `Attribute boUserIds is too short. Size is ${boUserIds.length}. Min size is 1.`
        );
      }
      if(boUserIds.length > 128) {
        throw new InvalidRequestBoUserIdsMaxSizeError(
          128,
          `Attribute boUserIds is too long. Size is ${boUserIds.length}. Max size is 128.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `AddBOUserToProjectRPC(projectId='${this.projectId}',boUserIds='${this.boUserIds}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `AddBOUserToProjectRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    MaxBoUsersReached = "MAX_BO_USERS_REACHED",
    BoUserAlreadyExists = "BO_USER_ALREADY_EXISTS",
    InvalidBoUserId = "INVALID_BO_USER_ID"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `AddBOUserToProjectRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `AddBOUserToProjectRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface MaxBoUsersReachedDTO {
      code: string;
    }
    
    export class MaxBoUsersReached extends LeoRPCError<RPCError> {
      readonly isMaxBoUsersReached: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.MaxBoUsersReached;
        super(code);
      }
    
    
      toDTO(): MaxBoUsersReachedDTO {
        const dto: MaxBoUsersReachedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    MaxBoUsersReached.prototype.toString = function toString(): string {
      return `AddBOUserToProjectRPC.MAX_BO_USERS_REACHED()`;
    };
    
    
    export interface BoUserAlreadyExistsDTO {
      code: string;
    }
    
    export class BoUserAlreadyExists extends LeoRPCError<RPCError> {
      readonly isBoUserAlreadyExists: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.BoUserAlreadyExists;
        super(code);
      }
    
    
      toDTO(): BoUserAlreadyExistsDTO {
        const dto: BoUserAlreadyExistsDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    BoUserAlreadyExists.prototype.toString = function toString(): string {
      return `AddBOUserToProjectRPC.BO_USER_ALREADY_EXISTS()`;
    };
    
    
    export interface InvalidBoUserIdDTO {
      code: string;
    }
    
    export class InvalidBoUserId extends LeoRPCError<RPCError> {
      readonly isInvalidBoUserId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidBoUserId;
        super(code);
      }
    
    
      toDTO(): InvalidBoUserIdDTO {
        const dto: InvalidBoUserIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidBoUserId.prototype.toString = function toString(): string {
      return `AddBOUserToProjectRPC.INVALID_BO_USER_ID()`;
    };
    

    export type Errors = InvalidProjectId | ProjectAlreadyArchived | MaxBoUsersReached | BoUserAlreadyExists | InvalidBoUserId;
    export type ErrorsDTO = InvalidProjectIdDTO | ProjectAlreadyArchivedDTO | MaxBoUsersReachedDTO | BoUserAlreadyExistsDTO | InvalidBoUserIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.MaxBoUsersReached:
          return new MaxBoUsersReached();
        case RPCError.BoUserAlreadyExists:
          return new BoUserAlreadyExists();
        case RPCError.InvalidBoUserId:
          return new InvalidBoUserId();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends AddBOUserToProjectRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestBoUserIdsError extends AddBOUserToProjectRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestBoUserIdsError";
  }
}

export class InvalidRequestBoUserIdsMaxSizeError extends InvalidRequestBoUserIdsError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestBoUserIdsMaxSizeError";
  }
}

export class InvalidRequestBoUserIdsMinSizeError extends InvalidRequestBoUserIdsError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestBoUserIdsMinSizeError";
  }
}


export class InvalidResponseInvalidProjectIdError extends AddBOUserToProjectRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends AddBOUserToProjectRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseMaxBoUsersReachedError extends AddBOUserToProjectRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseMaxBoUsersReachedError";
  }
}

export class InvalidResponseBoUserAlreadyExistsError extends AddBOUserToProjectRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseBoUserAlreadyExistsError";
  }
}

export class InvalidResponseInvalidBoUserIdError extends AddBOUserToProjectRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidBoUserIdError";
  }
}

